'use client';

import React, { useState } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';


const sections = [
  {
    id: 'understanding',
    title: 'Understanding Loans',
    icon: '◈',
    content: `A loan is a financial agreement in which a lender provides a borrower with a sum of money that must be repaid over time, typically with interest. Understanding the fundamental components of a loan — principal, interest rate, term, and fees — is essential before entering any borrowing arrangement.

**Principal** is the original amount borrowed. **Interest** is the cost of borrowing, expressed as an annual percentage rate (APR). The **term** is the length of time you have to repay the loan. **Fees** may include origination fees, prepayment penalties, and late payment charges.

Loans are broadly categorized as secured (backed by collateral, such as a home or vehicle) or unsecured (based solely on creditworthiness). Secured loans typically offer lower interest rates due to reduced lender risk.`,
  },
  {
    id: 'business',
    title: 'Business Loans',
    icon: '◆',
    content: `Business loans provide capital for companies at various stages — from startups seeking seed funding to established businesses pursuing expansion. Common business loan types include:

**Term Loans**: A lump sum repaid over a fixed period, ideal for equipment purchases, renovations, or working capital. Terms typically range from 1 to 10 years.

**SBA Loans**: Backed by the U.S. Small Business Administration, these loans offer favorable rates and terms for qualifying small businesses. The SBA 7(a) and 504 programs are the most widely used.

**Business Lines of Credit**: Flexible revolving credit that allows businesses to draw funds as needed, up to a set limit. Ideal for managing cash flow fluctuations.

**Invoice Financing**: Allows businesses to borrow against outstanding invoices, providing immediate liquidity without waiting for customer payments.

Lenders typically evaluate business loans based on time in business (usually 2+ years), annual revenue, credit score (both personal and business), and the purpose of the funds.`,
  },
  {
    id: 'personal',
    title: 'Personal Loans',
    icon: '◉',
    content: `Personal loans are unsecured loans that can be used for virtually any purpose — debt consolidation, home improvements, medical expenses, major purchases, or unexpected financial emergencies.

**Fixed-Rate Personal Loans**: The interest rate remains constant throughout the loan term, providing predictable monthly payments. Terms typically range from 12 to 84 months.

**Variable-Rate Personal Loans**: The interest rate fluctuates based on a benchmark index. These may start lower than fixed-rate loans but carry more risk over time.

**Debt Consolidation Loans**: A specific use of personal loans to combine multiple high-interest debts into a single, lower-rate payment — simplifying repayment and potentially reducing total interest paid.

Personal loan approval is primarily based on credit score, income, employment history, and existing debt obligations. Borrowers with credit scores above 680 generally qualify for the most competitive rates.`,
  },
  {
    id: 'healthcare',
    title: 'Healthcare Loans',
    icon: '◎',
    content: `Healthcare loans are specialized personal loans designed to cover medical, dental, vision, and other health-related expenses not fully covered by insurance. These loans provide a structured repayment alternative to high-interest medical credit cards or payment plans with variable terms.

**Elective Procedures**: Cosmetic surgery, LASIK, orthodontics, and fertility treatments are common uses for healthcare financing.

**Emergency Medical Expenses**: Unexpected hospitalizations, surgeries, or specialist treatments can create significant financial burdens. Healthcare loans provide immediate access to funds.

**Dental and Vision Care**: Major dental work (implants, crowns, full-mouth restoration) and corrective vision procedures are frequently financed through healthcare loans.

Many healthcare loan programs offer promotional 0% APR periods for qualified borrowers. It is important to understand what happens to the interest rate after the promotional period ends before committing to such a plan.`,
  },
  {
    id: 'approval',
    title: 'Improving Approval Chances',
    icon: '◐',
    content: `Improving your loan approval odds requires a strategic approach to your financial profile. The following steps can meaningfully increase your chances of approval and help you qualify for better rates:

**Check and Correct Your Credit Report**: Obtain free copies of your credit reports from all three bureaus (Equifax, Experian, TransUnion) at AnnualCreditReport.com. Dispute any inaccuracies, which can take 30–45 days to resolve.

**Reduce Your Debt-to-Income Ratio**: Lenders prefer a DTI below 43%. Pay down existing balances before applying, particularly on revolving credit accounts.

**Stabilize Your Employment**: Lenders favor consistent employment history. Avoid changing jobs immediately before applying for a loan.

**Increase Your Down Payment or Collateral**: For secured loans, offering more collateral or a larger down payment reduces lender risk and can improve your terms.

**Apply with a Co-Signer**: A creditworthy co-signer can significantly improve approval odds and interest rates for borrowers with limited or damaged credit histories.

**Avoid Multiple Hard Inquiries**: Each hard credit pull can temporarily lower your score by a few points. Rate-shop within a 14–45 day window, as multiple inquiries for the same loan type within this period are typically counted as one.`,
  },
  {
    id: 'responsible',
    title: 'Responsible Borrowing',
    icon: '◑',
    content: `Responsible borrowing means taking on debt thoughtfully, with a clear plan for repayment and a realistic assessment of your financial capacity. Before accepting any loan offer, consider the following:

**Borrow Only What You Need**: It can be tempting to accept the maximum approved amount, but borrowing more than necessary increases your total interest cost and repayment burden.

**Understand the Total Cost of the Loan**: Look beyond the monthly payment. Calculate the total amount you will repay over the life of the loan, including all interest and fees.

**Have a Repayment Plan**: Before signing, confirm that the monthly payment fits comfortably within your budget — ideally no more than 15–20% of your monthly take-home pay for all debt obligations combined.

**Build an Emergency Fund First**: If possible, maintain 3–6 months of living expenses in savings before taking on new debt. This protects you from defaulting if unexpected expenses arise.

**Communicate with Your Lender**: If you anticipate difficulty making a payment, contact your lender proactively. Many programs offer hardship accommodations, deferments, or modified payment plans.`,
  },
  {
    id: 'credit',
    title: 'Credit Score Basics',
    icon: '◒',
    content: `Your credit score is a three-digit number (typically ranging from 300 to 850) that represents your creditworthiness based on your credit history. The most widely used scoring model is the FICO Score.

**Score Ranges**:
- 800–850: Exceptional — qualifies for the best rates and terms
- 740–799: Very Good — qualifies for competitive rates
- 670–739: Good — qualifies for most standard loan programs
- 580–669: Fair — limited options; higher rates likely
- Below 580: Poor — may require secured products or co-signers

**What Affects Your Score**:
- Payment History (35%): The most significant factor. Even one missed payment can have a substantial negative impact.
- Credit Utilization (30%): The ratio of your current balances to your credit limits. Keep utilization below 30%.
- Length of Credit History (15%): Longer histories generally improve scores. Avoid closing old accounts unnecessarily.
- Credit Mix (10%): A mix of installment loans and revolving credit demonstrates responsible management.
- New Credit (10%): Multiple recent applications can temporarily lower your score.

Building or rebuilding credit takes time. Consistent on-time payments are the single most effective strategy.`,
  },
  {
    id: 'planning',
    title: 'Financial Planning Tips',
    icon: '◓',
    content: `Sound financial planning creates the foundation for successful borrowing and long-term financial health. The following principles apply whether you are preparing for a loan application or managing your finances more broadly:

**Create a Realistic Budget**: Track all income and expenses. Use the 50/30/20 rule as a starting point: 50% for needs, 30% for wants, 20% for savings and debt repayment.

**Prioritize High-Interest Debt**: If you carry multiple debts, focus additional payments on the highest-interest balances first (the avalanche method) to minimize total interest paid.

**Automate Savings and Payments**: Set up automatic transfers to savings accounts and automatic loan payments to avoid missed payments and build savings consistently.

**Review Your Financial Plan Annually**: Life circumstances change. Review your budget, insurance coverage, retirement contributions, and debt levels at least once per year.

**Understand Your Net Worth**: Calculate your assets minus your liabilities. Tracking this number over time provides a clear picture of your financial progress.

**Seek Professional Guidance When Needed**: A certified financial planner (CFP) can provide personalized guidance for complex financial situations. Lenders Home LLC does not provide financial, legal, or tax advice — consult qualified professionals for these matters.`,
  },
];

export default function LoanGuidePage() {
  const [activeSection, setActiveSection] = useState('understanding');

  const current = sections.find((s) => s.id === activeSection) || sections[0];

  const formatContent = (text: string) => {
    return text.split('\n\n').map((para, i) => {
      const formatted = para.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      return (
        <p key={i} className="text-slate-fog font-body leading-relaxed mb-4 last:mb-0" dangerouslySetInnerHTML={{ __html: formatted }} />
      );
    });
  };

  return (
    <main className="min-h-screen bg-cloud">
      <Header />

      {/* Hero */}
      <section className="pt-28 pb-16 px-6 lg:px-10 bg-white border-b border-slate-mist/30">
        <div className="max-w-5xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-sage-light text-sage-dark text-xs font-semibold font-body mb-6 tracking-wide uppercase">
            Educational Resource
          </div>
          <h1 className="font-heading text-display font-bold text-slate-dark leading-tight mb-6">
            The Lenders Home Loan Guide
          </h1>
          <p className="text-lg text-slate-fog font-body leading-relaxed max-w-3xl">
            An educational resource designed to help you understand your borrowing options, make informed decisions, and approach lending with confidence. Knowledge is the first step toward financial empowerment.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 px-6 lg:px-10">
        <div className="max-w-6xl mx-auto flex flex-col lg:flex-row gap-8">
          {/* Sidebar */}
          <aside className="lg:w-64 flex-shrink-0">
            <div className="bg-white rounded-2xl shadow-card border border-slate-mist/30 p-4 sticky top-24">
              <div className="text-xs font-semibold text-slate-fog font-body uppercase tracking-wide mb-3 px-2">Topics</div>
              <nav className="space-y-1">
                {sections.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setActiveSection(s.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-body text-left transition-all duration-200 ${
                      activeSection === s.id
                        ? 'bg-sky text-white font-semibold' :'text-slate-fog hover:bg-cloud hover:text-slate'
                    }`}
                  >
                    <span className="text-base">{s.icon}</span>
                    <span>{s.title}</span>
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Main content */}
          <div className="flex-1 min-w-0">
            <div className="bg-white rounded-3xl shadow-card border border-slate-mist/30 p-8 lg:p-10">
              <div className="flex items-center gap-3 mb-6">
                <span className="text-sky text-2xl">{current.icon}</span>
                <h2 className="font-heading text-2xl font-bold text-slate-dark">{current.title}</h2>
              </div>
              <div className="prose-custom">
                {formatContent(current.content)}
              </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-between mt-6">
              {(() => {
                const idx = sections.findIndex((s) => s.id === activeSection);
                return (
                  <>
                    <button
                      onClick={() => idx > 0 && setActiveSection(sections[idx - 1].id)}
                      disabled={idx === 0}
                      className="px-5 py-2.5 rounded-full text-sm font-semibold font-body border border-slate-mist text-slate-fog hover:border-sky/40 hover:text-slate disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                    >
                      ← Previous
                    </button>
                    <button
                      onClick={() => idx < sections.length - 1 && setActiveSection(sections[idx + 1].id)}
                      disabled={idx === sections.length - 1}
                      className="px-5 py-2.5 rounded-full text-sm font-semibold font-body bg-sky text-white hover:bg-sky-dark disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sky/30 shadow-md"
                    >
                      Next →
                    </button>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-8 px-6 lg:px-10 bg-white border-t border-slate-mist/30">
        <div className="max-w-5xl mx-auto">
          <p className="text-xs text-slate-fog font-body leading-relaxed">
            <strong>Educational Disclaimer:</strong> The information provided in this Loan Guide is for educational purposes only and does not constitute financial, legal, or tax advice. Loan terms, eligibility requirements, and interest rates vary by program, lender, and applicant profile. Consult a qualified financial professional before making borrowing decisions. Lenders Home LLC is not a direct lender and does not guarantee loan approval.
          </p>
        </div>
      </section>

      <Footer />
    </main>
  );
}
